import { useEffect, useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';

const Wrap = styled.div``;

const TopRow = styled.div`
  display: flex; justify-content: space-between; align-items: flex-end;
  margin-bottom: 16px;
  gap: 12px; flex-wrap: wrap;
`;
const Title = styled.h1` font-size: 18px; font-weight: 600; `;
const SubText = styled.span`
  font-size: 13px; color: ${({ theme }) => theme.colors.textSecondary};
  margin-left: 8px;
`;

const Btn = styled.button`
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 13px; font-weight: 500;
  border: 1px solid ${({ theme, $variant }) =>
    $variant === 'primary' ? theme.colors.primary : theme.colors.border};
  background: ${({ theme, $variant }) =>
    $variant === 'primary' ? theme.colors.primary : '#fff'};
  color: ${({ $variant }) => ($variant === 'primary' ? '#fff' : '#374151')};
  cursor: pointer;
  &:hover { opacity: 0.92; }
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 20px;
  @media (max-width: 768px) { grid-template-columns: repeat(2, 1fr); }
`;
const StatCard = styled.div`
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 10px;
  padding: 16px 18px;
`;
const StatLabel = styled.div`
  font-size: 12px; color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 6px;
`;
const StatValue = styled.div`
  font-size: 20px; font-weight: 600;
  color: ${({ $c }) => $c || '#1F2937'};
`;

const Tabs = styled.div`
  display: flex; gap: 4px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  margin-bottom: 16px;
`;
const Tab = styled.button`
  padding: 10px 18px;
  background: none;
  border: none;
  border-bottom: 2px solid ${({ $active, theme }) => ($active ? theme.colors.primary : 'transparent')};
  color: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.textSecondary)};
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  cursor: pointer;
`;

const FilterBar = styled.div` display: flex; gap: 10px; margin-bottom: 16px; `;
const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  font-size: 13px;
  background: #fff;
  cursor: pointer;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 10px;
  overflow: hidden;

  thead tr { background: #F9FAFB; }
  th, td {
    padding: 11px 14px;
    text-align: left;
    font-size: 13px;
  }
  th {
    font-weight: 500;
    color: ${({ theme }) => theme.colors.textSecondary};
    font-size: 12px;
  }
  tbody tr {
    border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
  }
`;

const Bar = styled.div`
  height: 18px;
  background: ${({ theme }) => theme.colors.primary};
  border-radius: 4px;
  min-width: 2px;
  width: ${({ $pct }) => $pct}%;
`;

const toDate = (ts) => {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  if (ts.seconds) return new Date(ts.seconds * 1000);
  if (typeof ts === 'string') return new Date(ts);
  return null;
};

const downloadCSV = (rows, filename) => {
  const csv = rows.map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const loadAll = async () => {
  const snap = await getDocs(collection(db, 'realtor_prospects'));
  const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

  // 부동산 이름 조인
  const cache = {};
  const resolveName = async (uid) => {
    if (!uid) return '';
    if (cache[uid] !== undefined) return cache[uid];
    try {
      const r = await getDoc(doc(db, 'realtors', uid));
      if (r.exists()) {
        const d = r.data();
        cache[uid] = d.business_name || d.businessName || '';
        return cache[uid];
      }
    } catch { /* skip */ }
    cache[uid] = '';
    return '';
  };

  return Promise.all(all.map(async (p) => ({
    ...p,
    realtorName: await resolveName(p.realtor_id),
  })));
};

const ProspectStats = () => {
  const [prospects, setProspects] = useState([]);
  const [tab, setTab] = useState('daily');
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setProspects(await loadAll()); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // 연도 리스트
  const years = useMemo(() => {
    const set = new Set([new Date().getFullYear()]);
    prospects.forEach((p) => {
      const d = toDate(p.created_at);
      if (d) set.add(d.getFullYear());
    });
    return [...set].sort((a, b) => b - a);
  }, [prospects]);

  // 통계
  const stats = useMemo(() => {
    const total = prospects.length;
    const thisYear = prospects.filter((p) => toDate(p.created_at)?.getFullYear() === year).length;
    const thisMonth = prospects.filter((p) => {
      const d = toDate(p.created_at);
      return d && d.getFullYear() === year && d.getMonth() + 1 === month;
    }).length;
    const converted = prospects.filter((p) => p.status === 'converted').length;
    return { total, thisYear, thisMonth, converted };
  }, [prospects, year, month]);

  // 집계 (일별)
  const dailyData = useMemo(() => {
    const daysInMonth = new Date(year, month, 0).getDate();
    const counts = Array.from({ length: daysInMonth }, (_, i) => ({ day: i + 1, count: 0 }));
    prospects.forEach((p) => {
      const d = toDate(p.created_at);
      if (d && d.getFullYear() === year && d.getMonth() + 1 === month) {
        counts[d.getDate() - 1].count += 1;
      }
    });
    return counts;
  }, [prospects, year, month]);

  // 집계 (월별)
  const monthlyData = useMemo(() => {
    const counts = Array.from({ length: 12 }, (_, i) => ({ month: i + 1, count: 0 }));
    prospects.forEach((p) => {
      const d = toDate(p.created_at);
      if (d && d.getFullYear() === year) {
        counts[d.getMonth()].count += 1;
      }
    });
    return counts;
  }, [prospects, year]);

  const rows = tab === 'daily' ? dailyData : monthlyData;
  const maxCount = Math.max(1, ...rows.map((r) => r.count));

  const handleExport = () => {
    const header = tab === 'daily'
      ? [`${year}년 ${month}월 일별 가망고객`, '건수']
      : [`${year}년 월별 가망고객`, '건수'];
    const data = rows.map((r) => [
      tab === 'daily' ? `${year}-${String(month).padStart(2, '0')}-${String(r.day).padStart(2, '0')}` : `${year}-${String(r.month).padStart(2, '0')}`,
      r.count,
    ]);
    downloadCSV([header, ...data], `가망고객_${tab}_${year}${tab === 'daily' ? `-${month}` : ''}.csv`);
  };

  return (
    <Wrap>
      <TopRow>
        <div>
          <Title>가망고객 DB 통계<SubText>부동산이 등록한 가망고객</SubText></Title>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn onClick={load}>{loading ? '불러오는 중...' : '새로고침'}</Btn>
          <Btn $variant="primary" onClick={handleExport}>CSV 내보내기</Btn>
        </div>
      </TopRow>

      <StatGrid>
        <StatCard>
          <StatLabel>전체 누적</StatLabel>
          <StatValue>{stats.total}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>{year}년 등록</StatLabel>
          <StatValue $c="#4A6CF7">{stats.thisYear}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>{year}년 {month}월 등록</StatLabel>
          <StatValue $c="#F59E0B">{stats.thisMonth}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>전환 완료</StatLabel>
          <StatValue $c="#22C55E">{stats.converted}</StatValue>
        </StatCard>
      </StatGrid>

      <Tabs>
        <Tab $active={tab === 'daily'} onClick={() => setTab('daily')}>일별</Tab>
        <Tab $active={tab === 'monthly'} onClick={() => setTab('monthly')}>월별</Tab>
      </Tabs>

      <FilterBar>
        <Select value={year} onChange={(e) => setYear(Number(e.target.value))}>
          {years.map((y) => <option key={y} value={y}>{y}년</option>)}
        </Select>
        {tab === 'daily' && (
          <Select value={month} onChange={(e) => setMonth(Number(e.target.value))}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>{m}월</option>
            ))}
          </Select>
        )}
      </FilterBar>

      <Table>
        <thead>
          <tr>
            <th style={{ width: 100 }}>{tab === 'daily' ? '일자' : '월'}</th>
            <th style={{ width: 80 }}>건수</th>
            <th>분포</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={tab === 'daily' ? r.day : r.month}>
              <td>{tab === 'daily' ? `${month}/${r.day}` : `${r.month}월`}</td>
              <td style={{ fontWeight: 500 }}>{r.count}</td>
              <td><Bar $pct={(r.count / maxCount) * 100} /></td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Wrap>
  );
};

export default ProspectStats;
